import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface BonusBalanceConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (description: string) => void;
  amount: number;
  currentBalance: number;
}

const BonusBalanceConfirmationModal: React.FC<BonusBalanceConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  amount,
  currentBalance
}) => {
  const theme = useTheme();
  const [description, setDescription] = useState('');

  const handleConfirm = () => {
    onConfirm(description);
    setDescription('');
    onClose();
  };

  const handleClose = () => {
    setDescription('');
    onClose();
  };

  const newBalance = currentBalance + amount;
  const isPositive = amount >= 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: 20,
          color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
        }}
      >
        Підтвердження операції
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
              mb: 2
            }}
          >
            Ви збираєтеся {isPositive ? 'зарахувати' : 'списати'}{' '}
            <strong style={{ color: isPositive ? '#4CAF50' : '#F44336' }}>
              {Math.abs(amount)} бонусів
            </strong>
          </Typography>
          
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
              borderRadius: 2,
              mb: 2
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              Поточний бонусний баланс: <strong>{currentBalance} бонусів</strong>
            </Typography>
            <Typography variant="body2">
              Новий бонусний баланс: <strong>{newBalance} бонусів</strong>
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Причина"
            placeholder="Наприклад: подарунок за активність"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1E1E1E'
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
            color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
            '&:hover': {
              borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
              backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'
            }
          }}
        >
          Скасувати
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#424242' : '#4ECDC4',
            color: theme.palette.mode === 'light' ? '#FFF' : '#000',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'light' ? '#616161' : '#3BA99F'
            }
          }}
        >
          Підтвердити
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BonusBalanceConfirmationModal;
